'use client';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ViewabilityConfig } from 'react-native';
// import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { isWeb, Spinner as TamaguiSpinner, View as TView } from 'tamagui';
import AnimatedHeader from './animatedHeader';
import { ItemObserver } from './observer';

export type RenderItemParams<T> = {
  item: T;
  index: number;
  type?: string | number;
};

export type UniversalListProps<T> = {
  data: T[];
  extraData?: any;
  renderItem: (
    params: RenderItemParams<T>
  ) => ReactElement<any, string | JSXElementConstructor<any>>;
  getItemType?: (item: T, index: number, extraData?: any) => string | number | undefined;
  keyExtractor?: (item: T, index: number) => string;
  contentContainerStyle?: any; // set height in horizontal mode here for web
  ListHeaderComponent?: React.ReactNode;
  ListHeaderComponentStyle?: any;
  ListFooterComponent?: React.ReactNode;
  ListFooterComponentStyle?: any;
  ListEmptyComponent?: React.ReactNode;
  ItemSeparatorComponent?: React.ComponentType<any>;
  onScroll?: (offset: number) => void;
  onMomentumScrollEnd?: (offset: number) => void;
  stickyHeaderHiddenOnScroll?: boolean;
  viewabilityConfig?: ViewabilityConfig;
  onViewableItemsChanged?: (info: {
    viewableItems: { item: T; index: number | null; key: string }[];
  }) => void;
  onEndReached?: () => void;
  // onFirstItemFullyVisible?: (info: { item: T; index: number | null; key: string }) => void;
  onEndReachedThreshold?: number; // native only
  scrollEventThrottle?: number;
  showsVerticalScrollIndicator?: boolean;
  refreshing?: boolean;
  onRefresh?: () => Promise<any> | void;
  estimatedItemSize?: number;
  horizontal?: boolean;
  headerHeight?: number;
  animatedHeader?: boolean; // for web, set to data.length
  columns?: number; // for web, default is 1
  gap?: string; // for web, default is 16px
  gridTemplateColumns?: string; // for web, default is 'repeat(1, 110px)'
} & React.RefAttributes<UniversalListRef>;

export type UniversalListRef = {
  scrollToIndex: (opts: { index: number; animated?: boolean; disableScrolling?: boolean }) => void;
  scrollToOffset: (opts: {
    offset: number;
    animated?: boolean;
    disableScrolling?: boolean;
  }) => void;
  recomputeViewableItems: () => void;
  scrollToTop: () => void;
  scrollToBottom: () => void;
  _rawRef: any;
};

function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number,
  options: { leading?: boolean } = {}
): T & { cancel: () => void } {
  const { leading = false } = options;
  let timeout: NodeJS.Timeout | null = null;
  let invoked = false;

  const debounced = (...args: any[]) => {
    if (leading && !invoked) {
      func(...args);
      invoked = true;
    }

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (!leading) func(...args);
      invoked = false;
    }, wait);
  };

  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
    invoked = false;
  };

  return debounced as T & { cancel: () => void };
}

const SCROLL_END_TIMEOUT = 300;

// const AnimatedHeader = dynamic(() => import('./animatedHeader'), {
//   ssr: false,
//   loading: () => null, // fallback
// });

const UniversalListInner: ForwardRefRenderFunction<UniversalListRef, UniversalListProps<any>> = (
  props,
  ref
) => {
  const {
    data,
    extraData,
    renderItem,
    getItemType,
    keyExtractor,
    contentContainerStyle,
    ListHeaderComponent,
    ListHeaderComponentStyle,
    ListFooterComponent,
    ListFooterComponentStyle,
    ListEmptyComponent,
    ItemSeparatorComponent,
    onScroll,
    onMomentumScrollEnd,
    stickyHeaderHiddenOnScroll = true,
    viewabilityConfig,
    onViewableItemsChanged,
    onEndReached,
    // onFirstItemFullyVisible,
    onEndReachedThreshold = 0.5,
    scrollEventThrottle = 16,
    showsVerticalScrollIndicator = true,
    refreshing,
    onRefresh,
    estimatedItemSize = 300,
    horizontal = false,
    headerHeight = 60,
    animatedHeader = false,
    columns = 1,
    gap = "16px",
    gridTemplateColumns = `repeat(${columns}, 110px)`,
  } = props;

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const flashListRef = useRef<any>(null);
  const isDragging = useRef(false);
  const isScrollingRef = useRef(false);
  const disableScrollingRef = useRef(false);
  const viewableItemsStateRef = useRef<any>({
    viewableItems: {},
    firstItemFullyVisible: {},
    itemsTracker: {},
    rangeTracker: [],
  });
  // const reY = useSharedValue(0);
  // const previousScrollY = useSharedValue(0);
  const [webRefresh, setWebRefresh] = useState(false);

  const [showHeader, setShowHeader] = useState(true);
  // const [prevScrollOffset, setPrevScrollOffset] = useState(0);
  const prevScrollOffset = useRef(0);

  const [dragOffset, setDragOffset] = useState(0);

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const pullThreshold = 60; // px

  const debouncedMomentumEnd = useRef(
    debounce((offset: number) => {
      if (isWeb) {
        const itemsInView = Object.keys(viewableItemsStateRef.current.itemsTracker) ?? [];
        const viewableItems = viewableItemsStateRef.current.rangeTracker.filter(
          (x) => itemsInView.indexOf(x.key) > -1
        );
        onViewableItemsChanged?.({ viewableItems });
      }

      // onFirstItemFullyVisible?.(viewableItems[0]);
      isScrollingRef.current = false;
      flashListRef.current?.recomputeViewableItems();
      onMomentumScrollEnd?.(offset);
    }, 300)
  ).current;

  const handleScroll = useCallback(
    (offset: number) => {
      isScrollingRef.current = true;
      if (onScroll) onScroll(offset);
      animateHeader(offset);

      if (disableScrollingRef.current) return;
      // console.log('Scroll event!!! Calling momentum end');
      debouncedMomentumEnd.cancel();
      debouncedMomentumEnd(offset);
    },
    [onScroll, stickyHeaderHiddenOnScroll]
  );

  const animateHeader = (currentY: number) => {
    if (stickyHeaderHiddenOnScroll && !horizontal) {
      if (currentY > prevScrollOffset.current && currentY > headerHeight) {
        // reY.value = withTiming(-headerHeight, { duration: 200 });
        setShowHeader(false);
      } else if (currentY < prevScrollOffset.current) {
        const difference = Math.abs(currentY - prevScrollOffset.current);
        const threshold = headerHeight;

        // console.log('Scrolling up!!!', currentY, difference, threshold, difference < threshold);
        if (difference < threshold) {
          return; // don't trigger animation unless threshold is met
        }

        // reY.value = withTiming(0, { duration: 200 });
        setShowHeader(true);
      }

      prevScrollOffset.current = currentY;
      // setPrevScrollOffset(currentY);
    }
  };

  // const headerStyle = useAnimatedStyle(() => {
  //   const hidden = reY.value < 0;
  //   return {
  //     height: withTiming(hidden ? 0 : headerHeight),
  //     transform: [
  //       {
  //         translateY: withTiming(hidden ? -headerHeight : 0),
  //       },
  //     ],
  //     overflow: 'hidden',
  //   };
  // }, [reY]);

  const webPullRefresh = useCallback(async () => {
    if (onRefresh) {
      setWebRefresh(true);
      await onRefresh();
      setWebRefresh(false);
    }
  }, [onRefresh]);

  useImperativeHandle(ref, () => {
    return {
      scrollToIndex: ({ index, animated = true, disableScrolling = false }) => {
        disableScrollingRef.current = disableScrolling;
        if (isWeb) virtuosoRef.current?.scrollToIndex({ index, behavior: 'smooth' });
        else flashListRef.current?.scrollToIndex({ index, animated });

        if (disableScrolling) {
          setTimeout(() => (disableScrollingRef.current = false), SCROLL_END_TIMEOUT);
        }
      },
      scrollToOffset: ({ offset, animated = true, disableScrolling = false }) => {
        if (isWeb) {
          virtuosoRef.current?.scrollTo({
            [horizontal ? 'left' : 'top']: offset,
            behavior: animated ? 'smooth' : 'auto',
          });
        } else {
          flashListRef.current?.scrollToOffset({ offset, animated });
        }
      },
      recomputeViewableItems: () => {
        if (!isWeb) flashListRef.current?.recomputeViewableItems?.();
      },
      scrollToTop: () => {
        if (isWeb) virtuosoRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        else flashListRef.current?.scrollToOffset({ offset: 0, animated: true });
      },
      scrollToBottom: () => {
        if (isWeb) virtuosoRef.current?.scrollToIndex({ index: data.length - 1 });
        else flashListRef.current?.scrollToEnd?.({ animated: true });
      },
      _rawRef: isWeb ? virtuosoRef.current : flashListRef.current,
    };
  }, [horizontal, data.length]);

  // const defaultHeight = typeof window !== 'undefined' ? window.innerHeight : 600;

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (container.scrollTop === 0) {
        isDragging.current = true;
        startY = e.touches[0].clientY;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging.current) return;
      const delta = e.touches[0].clientY - startY;
      if (delta > 0) {
        e.preventDefault(); // prevent native pull refresh
        setDragOffset(delta > 100 ? 100 : delta);
      }
    };

    const onTouchEnd = async () => {
      if (isDragging.current) {
        isDragging.current = false;

        if (dragOffset > pullThreshold && onRefresh) {
          setWebRefresh(true);
          setDragOffset(0);
          await onRefresh?.();
          setWebRefresh(false);
        } else {
          setDragOffset(0);
        }
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [onRefresh, dragOffset]);

  // Web
  const hideScrollbarStyle = showsVerticalScrollIndicator
    ? {}
    : {
        scrollbarWidth: 'none' as const,
        msOverflowStyle: 'none' as const,
      };

  return (
    <TView flex={1}>
      {ListHeaderComponent && animatedHeader && (
        <AnimatedHeader visible={showHeader} height={headerHeight}>
          {ListHeaderComponent}
        </AnimatedHeader>
      )}

      {/* {ListHeaderComponent && animatedHeader && isClient ? (
        <AnimatedHeader visible={showHeader} height={headerHeight}>
          {ListHeaderComponent}
        </AnimatedHeader>
      ) : ListHeaderComponent ? (
        <TView minHeight={headerHeight}>{ListHeaderComponent}</TView>
      ) : null} */}

      {data.length < 0 && (
        <TView
          height={dragOffset}
          alignItems="center"
          justifyContent="center"
          backgroundColor="$background"
        >
          {dragOffset > 10 || (refreshing && <TamaguiSpinner size="small" />)}
        </TView>
      )}

      <Virtuoso
        ref={virtuosoRef}
        data={data}
        initialItemCount={data.length}
        totalCount={data.length}
        style={horizontal ? { ...contentContainerStyle } : {}}
        // horizontal={horizontal}
        horizontalDirection={horizontal}
        atTopStateChange={(e) => handleScroll(0)}
        itemContent={(index, item) => {
          return (
            <ItemObserver
              key={keyExtractor?.(item, index) ?? index}
              item={item}
              index={index}
              onVisible={({ item, index }) => {
                const key = keyExtractor?.(item, index) ?? `${index}`;
                const visible = { item, index, key };
                viewableItemsStateRef.current.itemsTracker[index] = visible;
                // viewableItemsStateRef.current.firstItemFullyVisible = visible;
                // if (onViewableItemsChanged) {
                //   onViewableItemsChanged({
                //     viewableItems: Object.values(viewableItemsStateRef.current.viewableItems),
                //   });
                // }
                // if (onFirstItemFullyVisible) {
                //   onFirstItemFullyVisible(visible);
                // }
              }}
              onHidden={({ index }) => {
                delete viewableItemsStateRef.current.itemsTracker[index];
              }}
            >
              <>
                {renderItem({ item, index, type: getItemType?.(item, index) })}
                {ItemSeparatorComponent ? <ItemSeparatorComponent /> : <></>}
              </>
            </ItemObserver>

            // <>
            //   {renderItem({ item, index, type: getItemType?.(item, index) })}
            //   {ItemSeparatorComponent ? <ItemSeparatorComponent /> : <></>}
            // </>
          );
        }}
        components={{
          Header: () =>
            ListHeaderComponent && !animatedHeader ? (
              <TView style={ListHeaderComponentStyle}>{ListHeaderComponent}</TView>
            ) : null,
          Footer: () =>
            ListFooterComponent ? (
              <TView style={ListFooterComponentStyle}>{ListFooterComponent}</TView>
            ) : (
              <></>
            ),
          List:
            columns > 1 ? (
              React.forwardRef(
                (
                  { style, children }: { style?: React.CSSProperties; children?: React.ReactNode },
                  ref
                ) => (
                  <div
                    ref={ref}
                    style={{
                      ...style,
                      display: 'grid',
                      gridTemplateColumns: gridTemplateColumns,
                      gap: gap,
                    }}
                  >
                    {children}
                  </div>
                )
              )
            ) : (
             undefined
            ),
          EmptyPlaceholder: () => (ListEmptyComponent ? <>{ListEmptyComponent}</> : null),
        }}
        onScroll={(e) => {
          const target = e.target as HTMLElement;
          const offset = horizontal ? target.scrollLeft : target.scrollTop;
          handleScroll(offset);
        }}
        endReached={onEndReached}
        overscan={0}
        rangeChanged={({ startIndex, endIndex }) => {
          if (onViewableItemsChanged) {
            const viewableItems = data.slice(startIndex, endIndex + 1).map((item, i) => {
              const index = startIndex + i;
              const key = keyExtractor?.(item, index) ?? `${index}`;
              return { item, index, key };
            });

            // if (onViewableItemsChanged) {
            //   // onViewableItemsChanged({ viewableItems });

            // }

            viewableItemsStateRef.current.rangeTracker = viewableItems;

            // if (viewableItems.length > 0) {
            //   // onFirstItemFullyVisible(viewableItems[0]);
            //   viewableItemsStateRef.current.firstItemFullyVisible = viewableItems;
            // }
          }
        }}
        scrollerRef={(ref) => {
          if (ref && 'scrollTop' in ref) {
            scrollContainerRef.current = ref as HTMLElement;
          }
        }}
        defaultItemHeight={estimatedItemSize}
        atBottomStateChange={(atBottom) => {
          if (atBottom) {
            onEndReached?.();
          }
        }}
      />
    </TView>
  );
};

export const UniversalList = forwardRef(UniversalListInner) as <T>(
  props: UniversalListProps<T> & { ref?: React.Ref<UniversalListRef> }
) => ReactElement;
